import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import type { PagedSitios, Sitio, SitioCreateInput, SitioUpdateInput, TipoSitio } from "./schemas";

const KEY = "sitios";

export type SitiosFilters = { page?: number; size?: number; search?: string; tipo?: TipoSitio | "" };

function buildParams(f: SitiosFilters): Record<string, string | number> {
  const p: Record<string, string | number> = { page: f.page ?? 1, size: f.size ?? 20 };
  if (f.search) p.search = f.search;
  if (f.tipo) p.tipo = f.tipo;
  return p;
}

export function useListSitios(filters: SitiosFilters) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () => apiCall<PagedSitios>("get", "/api/v1/sitios", { searchParams: buildParams(filters) }),
    placeholderData: (prev) => prev,
  });
}

export function useSitio(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => apiCall<Sitio>("get", `/api/v1/sitios/${id}`),
    enabled: !!id,
  });
}

export function useCreateSitio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SitioCreateInput) => apiCall<Sitio>("post", "/api/v1/sitios", { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useUpdateSitio(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SitioUpdateInput) => apiCall<Sitio>("patch", `/api/v1/sitios/${id}`, { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useDeleteSitio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiCall<{ message: string }>("delete", `/api/v1/sitios/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}
